/**
 * Competency Library catalog.
 *
 * The canonical list of compliance competencies that CanniComply governs.
 * Grouped into 6 disciplines. Each entry is stable (id, name) so future
 * Competency Management APIs can persist references against these ids.
 *
 * Metric fields (assigned, completion, reviewDate, status, version) are
 * currently seeded deterministically at render time via a small utility
 * in the CompetencyLibrary component so cards feel populated. When the
 * backend Competency Management workflow ships, these values will be
 * replaced by live data keyed on the module id.
 */

import {
  Scale,
  Factory,
  ClipboardList,
  Database,
  HeartPulse,
  ClipboardCheck,
} from "lucide-react";

export const competencyCategories = [
  {
    id: "regulatory",
    name: "Regulatory Compliance",
    tagline: "Statutory frameworks the organisation is licensed against.",
    icon: Scale,
    accent: "gold",
    modules: [
      { id: "reg-ho-cd", name: "Home Office Controlled Drugs Licensing" },
      { id: "reg-mhra", name: "MHRA Licensing" },
      { id: "reg-cd-leg", name: "Controlled Drug Legislation" },
      { id: "reg-gphc", name: "GPhC Requirements" },
      { id: "reg-cqc", name: "CQC Awareness" },
    ],
  },
  {
    id: "quality-mgmt",
    name: "Quality Management",
    tagline: "The GxP and ISO controls that govern regulated operations.",
    icon: Factory,
    accent: "emerald",
    modules: [
      { id: "qm-gmp", name: "Good Manufacturing Practice (GMP)" },
      { id: "qm-gdp", name: "Good Distribution Practice (GDP)" },
      { id: "qm-gacp", name: "Good Agricultural and Collection Practice (GACP)" },
      { id: "qm-qms", name: "Quality Management Systems (QMS)" },
      { id: "qm-iso9001", name: "ISO 9001 Awareness" },
      { id: "qm-iso27001", name: "ISO 27001 Awareness" },
    ],
  },
  {
    id: "quality-processes",
    name: "Quality Processes",
    tagline: "Day-to-day quality operations every regulated team performs.",
    icon: ClipboardList,
    accent: "navy",
    modules: [
      { id: "qp-capa", name: "CAPA" },
      { id: "qp-risk", name: "Risk Management" },
      { id: "qp-change", name: "Change Control" },
      { id: "qp-deviation", name: "Deviation Management" },
      { id: "qp-validation", name: "Validation" },
      { id: "qp-supplier", name: "Supplier Qualification" },
      { id: "qp-doc-control", name: "Document Control" },
      { id: "qp-training", name: "Training Records" },
    ],
  },
  {
    id: "data-integrity",
    name: "Data Integrity",
    tagline: "Evidence a regulator can trust and reproduce.",
    icon: Database,
    accent: "gold",
    modules: [
      { id: "di-alcoa", name: "ALCOA+" },
      { id: "di-electronic", name: "Electronic Records" },
      { id: "di-audit-trail", name: "Audit Trails" },
      { id: "di-retention", name: "Record Retention" },
    ],
  },
  {
    id: "patient-safety",
    name: "Patient Safety",
    tagline: "Detection, reporting and recall obligations to patients.",
    icon: HeartPulse,
    accent: "rose",
    modules: [
      { id: "ps-pv", name: "Pharmacovigilance" },
      { id: "ps-complaints", name: "Product Complaints" },
      { id: "ps-adverse", name: "Adverse Events" },
      { id: "ps-recall", name: "Product Recall" },
    ],
  },
  {
    id: "inspection-readiness",
    name: "Inspection Readiness",
    tagline: "The disciplines that make an inspection uneventful.",
    icon: ClipboardCheck,
    accent: "emerald",
    modules: [
      { id: "ir-internal-audit", name: "Internal Auditing" },
      { id: "ir-mhra-inspection", name: "MHRA Inspection Readiness" },
      { id: "ir-evidence", name: "Evidence Collection" },
      { id: "ir-mock", name: "Mock Inspections" },
    ],
  },
];

export function totalModuleCount() {
  return competencyCategories.reduce(
    (acc, cat) => acc + cat.modules.length,
    0
  );
}
